import { useState, ChangeEvent } from 'react';
import {
	Spacer,
	Grid,
	Select,
	NumberField,
} from '@components';
import { TRateCardRules } from '@types';
import {
	RateCardSettingsTypes,
	RateCardFormErrors,
	RateCardTypes,
} from '@state';
import { useRecoilValue } from 'recoil';
import styles from './Rules.module.scss';
import { useClearRCSettingsError, useIsInRoute, useRateCardRulesState } from '@hooks';
import { getRateCardIndex } from '@utils';

type TProps = {
	isSettings?: boolean;
	isDisabled?: boolean;
}

export const CPKSettings = (props: TProps) => {
	const {
		isSettings = false,
		isDisabled = false,
	} = props;
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const globalInRoute = useIsInRoute('global');
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		isSettings: !!isSettings,
		type: 'CPK'
	});
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const [rateCardRules, setRateCardRules] = useRateCardRulesState();
	const rules = isSettings ? rateCardRules.settings : rateCardRules.rateCard;
	const [prices, setPrices] = useState({
		cpk_config_base_price: rules?.cpk_config_base_price ?? '',
		min_charge: rules?.min_charge ?? ''
	});
	const cubicConversionVals = [1000, 333, 250, 200, 190, 167, 150, 1];
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const updateRules = (e:ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const isNumber = !isNaN(parseFloat(value));
		let realVal = isNumber ? parseFloat(value) : null;
		if (['cpk_config_base_price', 'min_charge'].includes(name)) {
			setPrices({
				...prices,
				[name]: value
			});
			if (isNumber) realVal = parseFloat(parseFloat(value).toFixed(2));
		}
		// @ts-ignore
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.rules.${name}`)

		const update = {
			...rules,
			[name]: realVal
		} as TRateCardRules


		const newRules = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardRules(newRules);
	}

	const updateCubicConversion = (e: string) => {
		const update = {
			...rules,
			cpk_config_cubic_conversion: parseInt(e)
		} as TRateCardRules
		const newRules = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardRules(newRules);
		clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.rules.cpk_config_cubic_conversion`)
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.rules?.[key]?._errors?.[0] || '';
	}

	return (
		<div className={styles.cpkContainer}>
			<Grid columnTemplate='repeat(3, 1fr)'>
				<NumberField
					label={'Base Price'}
					name={'cpk_config_base_price'}
					value={`${prices.cpk_config_base_price ?? ''}`}
					onChange={updateRules}
					keepErrorSpace={false}
					placeholder={'0.00'}
					leftModule={<span className={`${styles.prefix} ${!prices.cpk_config_base_price && styles.prefixPlaceholder}`}>$</span>}
					disabled={isDisabled}
				/>
				<NumberField
					label={'Min. Charge'}
					name={'min_charge'}
					value={`${prices.min_charge ?? ''}`}
					onChange={updateRules}
					keepErrorSpace={false}
					placeholder={'0.00'}
					leftModule={<span className={`${styles.prefix} ${!prices.min_charge && styles.prefixPlaceholder}`}>$</span>}
					disabled={isDisabled}
					required
				/>
				<Select
					label={'Cubic conv.'}
					name={'cpk_config_cubic_conversion'}
					value={`${rules?.cpk_config_cubic_conversion ?? ''}`}
					onChange={updateCubicConversion}
					placeholder={'Cubic metres'}
					keepErrorSpace={false}
					disabled={isDisabled}
					hasError={!!hasError('cpk_config_cubic_conversion')}
					errorMessage={hasError('cpk_config_cubic_conversion')}
				>
					{
						rules?.cpk_config_cubic_conversion ?
							<Select.Option value={null}>Clear</Select.Option> : <></>
					}
					{cubicConversionVals.map((option, i) => {
						return <Select.Option key={i} value={option}>{option}</Select.Option>
					})}
				</Select>
			</Grid>
			{
				(rules?.cpk_config_cubic_conversion ||
				globalInRoute) ?
					<>
						<Spacer size={20} />
						<Grid columnTemplate='repeat(3, 1fr)'>
							<NumberField
								label={'Length'}
								name={'cpk_config_pallet_length'}
								value={`${rules?.cpk_config_pallet_length ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_length && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_length')}
								errorMessage={hasError('cpk_config_pallet_length')}
								disabled={isDisabled}
							/>
							<NumberField
								label={'Width'}
								name={'cpk_config_pallet_width'}
								value={`${rules?.cpk_config_pallet_width ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_width && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_width')}
								errorMessage={hasError('cpk_config_pallet_width')}
								disabled={isDisabled}
							/>
							<NumberField
								label={'Height'}
								name={'cpk_config_pallet_height'}
								value={`${rules?.cpk_config_pallet_height ?? ''}`}
								onChange={updateRules}
								keepErrorSpace={false}
								rightModule={<span className={`${styles.suffix} ${!rules?.cpk_config_pallet_height && styles.suffixPlaceholder}`}>m</span>}
								required
								hasError={!!hasError('cpk_config_pallet_height')}
								errorMessage={hasError('cpk_config_pallet_height')}
								disabled={isDisabled}
							/>
						</Grid>
					</>
					: null
			}
		</div>
	);
}
